import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/data/categories';

const categoryGroups = [
  {
    label: 'Obuwie',
    cats: categories.filter(c => ['shoes'].includes(c.id)),
  },
  {
    label: 'Odzież',
    cats: categories.filter(c => ['hoodies', 'tshirts', 'jackets', 'pants', 'jersey', 'sets'].includes(c.id)),
  },
  {
    label: 'Akcesoria',
    cats: categories.filter(c => ['headwear', 'accessories', 'bags', 'belts', 'perfumes'].includes(c.id)),
  },
  {
    label: 'Inne',
    cats: categories.filter(c => ['underwear', 'other'].includes(c.id)),
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-semibold whitespace-nowrap transition-colors ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            Kakobuy<span className="text-primary-500">Polska</span>Wiki
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Strona Główna
            </Link>
            <a
              href="#categories"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              Kategorie
            </a>
            <a
              href="#what-is"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              Czym jest?
            </a>
            <Link
              to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-book-open-line mr-1.5"></i>
              Poradnik Zakupowy
            </Link>
            <a
              href="#faq"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              FAQ
            </a>

            <div className="relative ml-3" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                <i className="ri-shopping-cart-line"></i>
                <span>Przeglądaj Produkty</span>
                <i className={`ri-arrow-down-s-line transition-transform text-sm ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600">
                        <i className={cat.icon}></i>
                      </span>
                      <span className="font-medium whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`ml-2 px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer bg-accent-500 text-white hover:bg-accent-600`}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 rounded-2xl border border-background-200/70 shadow-lg p-4 mt-2 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600">
                <i className="ri-home-4-line"></i>
              </span>
              Strona Główna
            </Link>
            <Link
              to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600">
                <i className="ri-book-open-line"></i>
              </span>
              Poradnik Zakupowy
            </Link>

            <div className="border-t border-background-200 my-2"></div>
            <p className="px-4 py-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
              Nawigacja
            </p>
            <a href="#categories" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500"><i className="ri-grid-line"></i></span>
              Kategorie
            </a>
            <a href="#what-is" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500"><i className="ri-lightbulb-line"></i></span>
              Czym jest Kakobuy?
            </a>
            <a href="#why-choose" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500"><i className="ri-star-line"></i></span>
              Zalety
            </a>
            <a href="#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500"><i className="ri-question-line"></i></span>
              FAQ
            </a>

            <div className="border-t border-background-200 my-2"></div>
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl cursor-pointer w-full text-left"
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600">
                  <i className="ri-shopping-bag-3-line"></i>
                </span>
                Kategorie Produktów (14)
              </div>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 border-l-2 border-primary-200 pl-4 space-y-4 mt-2 mb-1">
                {categoryGroups.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-2 px-3">
                      {group.label}
                    </p>
                    <div className="space-y-1">
                      {group.cats.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg cursor-pointer"
                        >
                          <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 text-xs">
                            <i className={cat.icon}></i>
                          </span>
                          <span className="font-medium text-xs">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-2"></div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full cursor-pointer"
            >
              <i className="ri-shopping-cart-line"></i>
              Kakobuy Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold bg-accent-500 text-white rounded-full cursor-pointer"
            >
              <i className="ri-shopping-bag-line"></i>
              Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}